import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPut, apiPost } from '../utils/api';

interface TelegramMessage {
  id: string;
  playerId: string;
  chatId: string;
  messageId: string | null;
  direction: 'INCOMING' | 'OUTGOING';
  type: string;
  text: string;
  metadata: string | null;
  taskId: string | null;
  tournamentId: string | null;
  matchId: string | null;
  availabilityId: string | null;
  status: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  player: {
    id: string;
    name: string;
    telegramUsername: string | null;
    telegramChatId: string | null;
  };
  task?: {
    id: string;
    activePlayer: { id: string; name: string };
    opponentPlayer: { id: string; name: string };
    tournament: { id: string; name: string };
    group: { id: string; name: string; level: string };
  };
  tournament?: {
    id: string;
    name: string;
  };
  match?: {
    id: string;
    matchCode: string | null;
    player1: { id: string; name: string } | null;
    player2: { id: string; name: string } | null;
  };
}

interface BotStatus {
  initialized: boolean;
  tokenConfigured: boolean;
  status: 'RUNNING' | 'NOT_INITIALIZED' | 'NO_TOKEN';
}

const TelegramLogs: React.FC = () => {
  const [messages, setMessages] = useState<TelegramMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesError, setMessagesError] = useState('');
  const [botStatus, setBotStatus] = useState<BotStatus | null>(null);
  // Восстанавливаем настройки автообновления из localStorage
  const [autoRefresh, setAutoRefresh] = useState(() => {
    const saved = localStorage.getItem('telegramLogs_autoRefresh');
    return saved === 'true';
  });
  const [refreshInterval, setRefreshInterval] = useState(() => {
    const saved = localStorage.getItem('telegramLogs_refreshInterval');
    const interval = saved ? parseInt(saved, 10) : 60; // период в секундах (увеличено до 60 по умолчанию)
    // Минимальный интервал - 60 секунд, чтобы не перегружать API
    return interval < 60 ? 60 : interval;
  });
  const [showPlayerAssignModal, setShowPlayerAssignModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<TelegramMessage | null>(null);
  const [showMessageDetailsModal, setShowMessageDetailsModal] = useState(false);
  const [selectedMessageDetails, setSelectedMessageDetails] = useState<TelegramMessage | null>(null);
  const [players, setPlayers] = useState<any[]>([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playersSearch, setPlayersSearch] = useState('');
  const [playersSearchDebounced, setPlayersSearchDebounced] = useState('');
  const [playersNoIdOnly, setPlayersNoIdOnly] = useState(false);
  const [playerAccountsMap, setPlayerAccountsMap] = useState<Record<string, any[]>>({});
  const [playersPagination, setPlayersPagination] = useState({
    page: 1,
    limit: 200,
    total: 0,
    pages: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    direction: '',
    type: '',
    playerId: '',
    search: '',
    unknownOnly: false
  });

  const fetchBotStatus = useCallback(async () => {
    try {
      const status = await apiGet<BotStatus>('/telegram-messages/bot-status');
      setBotStatus(status);
    } catch (error) {
      console.error('Ошибка загрузки статуса бота:', error);
    }
  }, []);

  const fetchMessages = useCallback(async (page = pagination.page, limit = pagination.limit) => {
    try {
      setLoading(true);
      setMessagesError('');
      const normalizedSearch = filters.search.trim();
      const isUnfilteredFirstPage =
        page === 1 &&
        !filters.direction &&
        !filters.type &&
        !filters.playerId &&
        !normalizedSearch &&
        !filters.unknownOnly;

      // Retry-защита:
      // иногда первая загрузка может вернуться пустой/с transient ошибкой,
      // хотя данные в БД есть. Повторяем до 3 попыток.
      let data: { messages: TelegramMessage[]; pagination: any } | null = null;
      let lastError: any = null;

      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const params = new URLSearchParams();
          if (filters.direction) params.append('direction', filters.direction);
          if (filters.type) params.append('type', filters.type);
          if (filters.playerId) params.append('playerId', filters.playerId);
          if (normalizedSearch) params.append('search', normalizedSearch);
          if (filters.unknownOnly) params.append('unknownOnly', 'true');
          params.append('page', page.toString());
          params.append('limit', limit.toString());
          // cache-buster для промежуточных прокси/клиентского кеша
          params.append('_ts', Date.now().toString());

          const current = await apiGet<{ messages: TelegramMessage[]; pagination: any }>(
            `/telegram-messages?${params}`
          );

          const currentMessages = Array.isArray(current?.messages) ? current.messages : [];
          const shouldRetryEmpty = isUnfilteredFirstPage && currentMessages.length === 0 && attempt < 2;

          data = current;
          if (!shouldRetryEmpty) break;

          await new Promise((resolve) => setTimeout(resolve, 350));
        } catch (e) {
          lastError = e;
          if (attempt === 2) {
            throw e;
          }
          await new Promise((resolve) => setTimeout(resolve, 350));
        }
      }

      if (!data && lastError) {
        throw lastError;
      }

      const resolvedData = data || { messages: [], pagination: {} };
      setMessages(resolvedData.messages || []);
      setPagination(prev => ({
        ...prev,
        page: resolvedData.pagination?.page || page,
        limit: resolvedData.pagination?.limit || limit,
        total: resolvedData.pagination?.total || 0,
        pages: resolvedData.pagination?.pages || 0
      }));
    } catch (error: any) {
      // Если ошибка 429, не логируем как критическую (это временная проблема)
      if (error.message && error.message.includes('429')) {
        console.warn('Rate limit: слишком много запросов. Подождите немного...');
        setMessagesError('Слишком много запросов. Подождите немного и обновите страницу.');
      } else {
        console.error('Ошибка загрузки логов Telegram:', error);
        setMessagesError(error?.message || 'Ошибка загрузки логов Telegram');
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Явная первичная загрузка при открытии страницы.
  // Это дополнительная страховка на случай, если debounce-цикл фильтров не сработал.
  useEffect(() => {
    fetchBotStatus();
    fetchMessages(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Сохраняем настройки автообновления в localStorage при их изменении
  useEffect(() => {
    localStorage.setItem('telegramLogs_autoRefresh', autoRefresh.toString());
  }, [autoRefresh]);

  useEffect(() => {
    localStorage.setItem('telegramLogs_refreshInterval', refreshInterval.toString());
  }, [refreshInterval]);

  // Debounce для поиска
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchBotStatus();
      const currentLimit = pagination.limit;
      setPagination(prev => ({ ...prev, page: 1 })); // Сбрасываем на первую страницу при изменении фильтров
      fetchMessages(1, currentLimit);
    }, filters.search ? 500 : 0); // Задержка только для поиска

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Автообновление логов
  useEffect(() => {
    if (!autoRefresh) return;

    // Убеждаемся, что интервал не меньше 60 секунд
    const safeInterval = refreshInterval < 60 ? 60 : refreshInterval;

    const interval = setInterval(() => {
      // Делаем запросы с небольшой задержкой между ними, чтобы не перегружать API
      fetchBotStatus();
      setTimeout(() => {
        fetchMessages(pagination.page, pagination.limit);
      }, 500); // Задержка 500мс между запросами
    }, safeInterval * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, refreshInterval, pagination.page, pagination.limit]);

  // Загрузка игроков для модального окна (поиск)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPlayersSearchDebounced(playersSearch.trim());
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [playersSearch]);

  useEffect(() => {
    if (!showPlayerAssignModal) return;
    
    const loadPlayers = async () => {
      try {
        setPlayersLoading(true);
        const params = new URLSearchParams();
        params.append('limit', '500'); // Увеличиваем лимит до 500
        params.append('isActive', 'true');
        if (playersSearchDebounced) {
          params.append('search', playersSearchDebounced);
          params.append('page', '1'); // При поиске всегда с первой страницы
        } else {
          params.append('page', playersPagination.page.toString());
        }

        const data = await apiGet<{ players: any[]; pagination: any }>(`/players?${params}`);
        const loadedPlayers = data.players || [];
        setPlayers(loadedPlayers);
        setPlayersPagination(prev => ({
          ...prev,
          page: data.pagination?.page || 1,
          total: data.pagination?.total || 0,
          pages: data.pagination?.pages || 0
        }));

        // Важно: загружаем Telegram-аккаунты ОДНИМ запросом, чтобы
        // не создавать N+1 запросов /players/:id/telegram-accounts на каждую строку.
        try {
          const accountsData = await apiGet<{ accounts: any[] }>('/players/telegram-accounts/all');
          const allAccounts = Array.isArray(accountsData?.accounts) ? accountsData.accounts : [];
          const playerIdsSet = new Set(loadedPlayers.map((p: any) => p.id));
          const map: Record<string, any[]> = {};
          allAccounts.forEach((acc: any) => {
            const pid = acc?.playerId;
            if (!pid || !playerIdsSet.has(pid)) return;
            if (!map[pid]) map[pid] = [];
            map[pid].push(acc);
          });
          setPlayerAccountsMap(map);
        } catch (accountsError) {
          console.error('Ошибка загрузки списка Telegram-аккаунтов:', accountsError);
          setPlayerAccountsMap({});
        }
      } catch (error) {
        console.error('Ошибка загрузки игроков:', error);
      } finally {
        setPlayersLoading(false);
      }
    };
    
    loadPlayers();
  }, [playersSearchDebounced, showPlayerAssignModal, playersPagination.page]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      fetchMessages(newPage, pagination.limit);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
    fetchMessages(1, newLimit);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    return (
      <>
        <div>{dateStr}</div>
        <div className="text-gray-500">{timeStr}</div>
      </>
    );
  };

  const isUnregisteredPlayer = (message: TelegramMessage): boolean => {
    // Игрок считается незарегистрированным, если его имя в базе - "Незарегистрированные пользователи (Telegram)"
    return message.player.name === 'Незарегистрированные пользователи (Telegram)';
  };

  const isBotPlayer = (message: TelegramMessage): boolean => {
    // Игрок считается ботом, если его имя начинается с "Telegram Bot"
    return message.player.name?.startsWith('Telegram Bot') || false;
  };

  const getPlayerDisplayName = (message: TelegramMessage): string => {
    // Приоритет: 1. Имя игрока из базы (если не системный/незарегистрированный), 2. Имя и фамилия из Telegram + @username в скобках, 3. "Неизвестный"
    if (message.player.name && message.player.name !== 'Незарегистрированные пользователи (Telegram)') {
      return message.player.name;
    }

    // Извлекаем данные из metadata для незарегистрированных пользователей
    let firstName: string | undefined;
    let lastName: string | undefined;
    let username: string | undefined;

    if (message.metadata) {
      try {
        const meta = JSON.parse(message.metadata);
        
        // Проверяем userInfo (для приватных сообщений)
        if (meta.userInfo) {
          firstName = meta.userInfo.firstName;
          lastName = meta.userInfo.lastName;
          username = meta.userInfo.username;
        }
        
        // Проверяем message.from (для сообщений из групп)
        if (!firstName && !lastName && !username && meta.message?.from) {
          firstName = meta.message.from.first_name;
          lastName = meta.message.from.last_name;
          username = meta.message.from.username;
        }
        
        // Проверяем poll_answer.user (для ответов на опросы)
        if (!firstName && !lastName && !username && meta.poll_answer?.user) {
          firstName = meta.poll_answer.user.first_name;
          lastName = meta.poll_answer.user.last_name;
          username = meta.poll_answer.user.username;
        }
      } catch (e) {
        // Игнорируем ошибки парсинга
      }
    }

    // Формируем имя из Telegram
    let name = '';
    if (firstName && lastName) {
      name = `${firstName} ${lastName}`;
    } else if (firstName) {
      name = firstName;
    } else if (lastName) {
      name = lastName;
    }

    // Добавляем username в скобках, если есть
    if (name && username) {
      return `${name} (@${username})`;
    } else if (name) {
      return name;
    } else if (username) {
      return `@${username}`;
    } else {
      // Если нет имени и username, показываем Chat ID + username (если есть)
      // Для системных сообщений chatId может быть "system"
      // Пытаемся еще раз найти username в metadata (на случай, если он был в poll_answer.user)
      // username уже был проверен выше и не найден, поэтому ищем в metadata заново
      let foundUsername: string | undefined;
      if (message.metadata) {
        try {
          const meta = JSON.parse(message.metadata);
          foundUsername = meta.poll_answer?.user?.username || 
                         meta.message?.from?.username || 
                         meta.userInfo?.username;
        } catch (e) {
          // Игнорируем ошибки парсинга
        }
      }
      
      // Если есть chatId, показываем его (даже если нет имени и username)
      if (message.chatId && message.chatId !== 'system') {
        if (foundUsername) {
          return `${message.chatId} (@${foundUsername})`;
        } else {
          return `Chat ID: ${message.chatId}`;
        }
      } else {
        // Если chatId = "system" или отсутствует
        if (foundUsername) {
          return `@${foundUsername}`;
        }
        return message.chatId || 'Неизвестный';
      }
    }
  };

  const getDirectionBadge = (direction: string) => {
    if (direction === 'INCOMING') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Входящее</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Исходящее</span>;
  };

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'NOTIFICATION': 'Уведомление',
      'REQUEST': 'Запрос',
      'RESPONSE': 'Ответ',
      'BUTTON_CLICK': 'Нажатие кнопки',
      'TEXT_MESSAGE': 'Текстовое сообщение',
      'COMMAND': 'Команда',
      'CALLBACK_QUERY': 'Callback'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status: string | null) => {
    if (!status) return null;
    const styles: { [key: string]: string } = {
      'SENT': 'bg-blue-100 text-blue-800',
      'DELIVERED': 'bg-green-100 text-green-800',
      'READ': 'bg-gray-100 text-gray-800',
      'FAILED': 'bg-red-100 text-red-800'
    };
    const labels: { [key: string]: string } = {
      'SENT': 'Отправлено',
      'DELIVERED': 'Доставлено',
      'READ': 'Прочитано',
      'FAILED': 'Ошибка'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getBotStatusBadge = (status: BotStatus) => {
    const styles = {
      'RUNNING': 'bg-green-100 text-green-800 border-green-300',
      'NOT_INITIALIZED': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'NO_TOKEN': 'bg-red-100 text-red-800 border-red-300'
    };
    const labels = {
      'RUNNING': '✅ Работает',
      'NOT_INITIALIZED': '⚠️ Не инициализирован',
      'NO_TOKEN': '❌ Токен не настроен'
    };
    const icons = {
      'RUNNING': '🟢',
      'NOT_INITIALIZED': '🟡',
      'NO_TOKEN': '🔴'
    };
    
    return (
      <div className={`inline-flex items-center px-4 py-2 rounded-lg border ${styles[status.status]}`}>
        <span className="mr-2">{icons[status.status]}</span>
        <span className="font-medium">{labels[status.status]}</span>
      </div>
    );
  };

  const handleRefresh = () => {
    fetchBotStatus();
    fetchMessages(pagination.page, pagination.limit);
  };

  const handleAssignToPlayer = (message: TelegramMessage) => {
    setSelectedMessage(message);
    setShowPlayerAssignModal(true);
    setPlayersSearch('');
    setPlayersNoIdOnly(false);
    setPlayersPagination({ page: 1, limit: 200, total: 0, pages: 0 });
  };

  const handleAssignPlayer = async (playerId: string) => {
    if (!selectedMessage) return;

    try {
      const meta = selectedMessage.metadata ? JSON.parse(selectedMessage.metadata) : {};
      
      // Извлекаем Chat ID и username из разных источников
      let chatId: string | undefined;
      let username: string | undefined;
      let firstName: string | undefined;
      let lastName: string | undefined;
      
      // Для приватных сообщений
      if (meta.userInfo) {
        chatId = meta.userInfo.chatId;
        username = meta.userInfo.username;
        firstName = meta.userInfo.firstName;
        lastName = meta.userInfo.lastName;
      }
      
      // Для сообщений из групп
      if (!chatId && meta.message?.chat) {
        // Для приватных чатов используем chat.id, для групп - from.id
        if (meta.message.chat.type === 'private') {
          chatId = meta.message.chat.id?.toString();
        } else if (meta.message.from) {
          chatId = meta.message.from.id?.toString();
        }
      }
      
      // Если не нашли в metadata, используем chatId из сообщения
      if (!chatId) {
        chatId = selectedMessage.chatId;
      }
      
      // Извлекаем username, firstName, lastName из message.from для групп
      if (meta.message?.from) {
        if (!username && meta.message.from.username) {
          username = meta.message.from.username;
        }
        if (!firstName && meta.message.from.first_name) {
          firstName = meta.message.from.first_name;
        }
        if (!lastName && meta.message.from.last_name) {
          lastName = meta.message.from.last_name;
        }
      }
      
      if (!chatId) {
        alert('Не удалось определить Chat ID');
        return;
      }
      
      // Используем кеш загруженных аккаунтов, чтобы не перегружать API.
      const existingAccounts = playerAccountsMap[playerId] || [];
      const isPrimary = existingAccounts.length === 0; // Первый аккаунт делаем основным
      
      await apiPost(`/players/${playerId}/telegram-accounts`, {
        chatId: chatId,
        username: username || undefined,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        isPrimary: isPrimary
      });

      // Обновляем логи
      await fetchMessages(pagination.page, pagination.limit);
      setPlayerAccountsMap((prev) => {
        const prevList = prev[playerId] || [];
        const nextAccount = {
          id: `local_${Date.now()}`,
          playerId,
          chatId: chatId,
          username: username || null,
          firstName: firstName || null,
          lastName: lastName || null,
          isPrimary,
        };
        return {
          ...prev,
          [playerId]: [...prevList, nextAccount],
        };
      });
      setShowPlayerAssignModal(false);
      setSelectedMessage(null);
      setPlayersSearch('');
      setPlayersPagination({ page: 1, limit: 200, total: 0, pages: 0 });
    } catch (error: any) {
      console.error('Ошибка присвоения Chat ID игроку:', error);
      const errorMessage = error.response?.data?.error || 'Ошибка присвоения Chat ID. Возможно, этот Chat ID уже используется другим игроком.';
      alert(errorMessage);
    }
  };

  if (loading && messages.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">📱 Логи Telegram</h1>
        <div className="flex items-center gap-4">
          {botStatus && (
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Статус бота:</span>
              </div>
              {getBotStatusBadge(botStatus)}
            </div>
          )}
          
          {/* Настройки автообновления */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span>Автообновление</span>
              </label>
              {autoRefresh && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">каждые</span>
                  <input
                    type="number"
                    min="5"
                    max="300"
                    value={refreshInterval}
                    onChange={(e) => setRefreshInterval(Math.max(5, Math.min(300, parseInt(e.target.value) || 30)))}
                    className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">сек</span>
                </div>
              )}
            </div>
            
            {/* Кнопка обновления */}
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Обновить
            </button>
          </div>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Игрок / Chat ID / Telegram
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Поиск по имени, Chat ID, username..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Направление
            </label>
            <select
              value={filters.direction}
              onChange={(e) => setFilters({ ...filters, direction: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Все</option>
              <option value="INCOMING">Входящие</option>
              <option value="OUTGOING">Исходящие</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Тип
            </label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Все типы</option>
              <option value="NOTIFICATION">Уведомление</option>
              <option value="REQUEST">Запрос</option>
              <option value="RESPONSE">Ответ</option>
              <option value="BUTTON_CLICK">Нажатие кнопки</option>
              <option value="TEXT_MESSAGE">Текстовое сообщение</option>
              <option value="COMMAND">Команда</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer pb-2">
              <input
                type="checkbox"
                checked={filters.unknownOnly}
                onChange={(e) => setFilters({ ...filters, unknownOnly: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span>Неизвестные</span>
            </label>
          </div>
        </div>
      </div>

      {/* Список сообщений */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {messagesError && (
          <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {messagesError}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="divide-y divide-gray-200" style={{ width: '100%', tableLayout: 'fixed' }}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '80px' }}>
                  Дата
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '280px' }}>
                  Игрок
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '120px' }}>
                  Направление
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '140px' }}>
                  Тип
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '300px' }}>
                  Сообщение
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '120px' }}>
                  Статус
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '100px' }}>
                  Контекст
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {messages.map((message) => (
                <tr 
                  key={message.id} 
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedMessageDetails(message);
                    setShowMessageDetailsModal(true);
                  }}
                >
                  <td className="px-6 py-4 text-sm text-gray-900" style={{ width: '80px' }}>
                    {formatDate(message.createdAt)}
                  </td>
                  <td className="px-6 py-4" style={{ width: '280px' }}>
                    <div className="text-sm font-medium text-gray-900">
                      {isUnregisteredPlayer(message) ? (
                        <span
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAssignToPlayer(message);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          title="Нажмите, чтобы присвоить Chat ID и username игроку"
                        >
                          {getPlayerDisplayName(message)}
                        </span>
                      ) : (
                        getPlayerDisplayName(message)
                      )}
                      {isUnregisteredPlayer(message) && (
                        <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          Незарегистрирован
                        </span>
                      )}
                      {isBotPlayer(message) && message.direction === 'OUTGOING' && (
                        <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          🤖 Бот
                        </span>
                      )}
                    </div>
                    {message.player.telegramUsername && !isUnregisteredPlayer(message) && (
                      <div className="text-sm text-gray-500">{message.player.telegramUsername}</div>
                    )}
                    {/* Показываем chatId, если игрок не идентифицирован или если имя равно chatId */}
                    {(() => {
                      // Показываем chatId, если:
                      // 1. Игрок незарегистрированный
                      // 2. Имя игрока равно chatId (значит, имя не было найдено)
                      // 3. Имя игрока пустое
                      const shouldShowChatId = isUnregisteredPlayer(message) || 
                                               message.player.name === message.chatId ||
                                               !message.player.name || 
                                               message.player.name.trim() === '';
                      
                      if (!shouldShowChatId || !message.chatId || message.chatId === 'system') {
                        return null;
                      }
                      
                      // Пытаемся извлечь username из metadata
                      let username: string | undefined;
                      if (message.metadata) {
                        try {
                          const meta = JSON.parse(message.metadata);
                          username = meta.userInfo?.username || 
                                    meta.message?.from?.username || 
                                    meta.poll_answer?.user?.username;
                        } catch (e) {
                          // Игнорируем ошибки парсинга
                        }
                      }
                      
                      return (
                        <div className="text-xs text-gray-500 mt-1">
                          <div>Chat ID: {message.chatId}</div>
                          {username && !getPlayerDisplayName(message).includes(`@${username}`) && (
                            <div>@{username}</div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4" style={{ width: '120px' }}>
                    <div className="whitespace-nowrap">{getDirectionBadge(message.direction)}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500" style={{ width: '140px' }}>
                    <div className="whitespace-nowrap">{getTypeLabel(message.type)}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900" style={{ width: '300px' }}>
                    <div className="truncate" title={message.text}>
                      {message.text}
                    </div>
                    {message.errorMessage && (
                      <div className="text-xs text-red-600 mt-1 break-words">
                        Ошибка: {message.errorMessage}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4" style={{ width: '120px' }}>
                    <div className="whitespace-nowrap">{getStatusBadge(message.status)}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500" style={{ width: '100px' }}>
                    {message.task && (
                      <div className="truncate" title={`Задача: ${message.task.activePlayer.name} vs ${message.task.opponentPlayer.name} - ${message.task.tournament.name}`}>
                        <div className="font-medium truncate">Задача</div>
                        <div className="text-xs truncate">{message.task.tournament.name}</div>
                      </div>
                    )}
                    {message.tournament && !message.task && (
                      <div className="truncate" title={`Турнир: ${message.tournament.name}`}>
                        Турнир: {message.tournament.name}
                      </div>
                    )}
                    {message.match && (
                      <div className="truncate" title={`Матч: ${message.match.matchCode || `${message.match.player1?.name} vs ${message.match.player2?.name}`}`}>
                        Матч: {message.match.matchCode || 'Матч'}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {messages.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">Сообщения не найдены</p>
        </div>
      )}

      {/* Пагинация и информация */}
      {pagination.total > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Информация о количестве */}
            <div className="text-sm text-gray-600">
              Показано <span className="font-medium text-gray-900">{((pagination.page - 1) * pagination.limit) + 1}</span> - <span className="font-medium text-gray-900">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> из <span className="font-medium text-gray-900">{pagination.total}</span> сообщений
            </div>

            {/* Выбор количества на странице */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Показывать:</label>
              <select
                value={pagination.limit}
                onChange={(e) => handleLimitChange(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>

            {/* Навигация по страницам */}
            {pagination.pages > 1 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Первая страница"
                >
                  ««
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Предыдущая страница"
                >
                  ‹
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(7, pagination.pages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.pages <= 7) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 4) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.pages - 3) {
                      pageNum = pagination.pages - 6 + i;
                    } else {
                      pageNum = pagination.page - 3 + i;
                    }
                    
                    if (pageNum < 1 || pageNum > pagination.pages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-md min-w-[40px] ${
                          pageNum === pagination.page
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Следующая страница"
                >
                  ›
                </button>
                <button
                  onClick={() => handlePageChange(pagination.pages)}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Последняя страница"
                >
                  »»
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Модальное окно для присвоения Chat ID игроку */}
      {showPlayerAssignModal && selectedMessage && (() => {
        const meta = selectedMessage.metadata ? JSON.parse(selectedMessage.metadata) : {};
        
        // Извлекаем Chat ID и username из разных источников
        let chatId: string | undefined;
        let username: string | undefined;
        let displayName: string = getPlayerDisplayName(selectedMessage);
        
        // Для приватных сообщений
        if (meta.userInfo) {
          chatId = meta.userInfo.chatId;
          username = meta.userInfo.username;
        }
        
        // Для сообщений из групп
        if (!chatId && meta.message?.chat) {
          // Для приватных чатов используем chat.id, для групп - from.id
          if (meta.message.chat.type === 'private') {
            chatId = meta.message.chat.id?.toString();
          } else if (meta.message.from) {
            chatId = meta.message.from.id?.toString();
          }
        }
        
        // Если не нашли в metadata, используем chatId из сообщения
        if (!chatId) {
          chatId = selectedMessage.chatId;
        }
        
        // Извлекаем username из message.from для групп
        if (!username && meta.message?.from?.username) {
          username = meta.message.from.username;
        }
        
        const playersForRender = playersNoIdOnly
          ? players.filter((player) => {
              const accounts = playerAccountsMap[player.id] || [];
              return accounts.length === 0 && !player.telegramChatId;
            })
          : players;

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Присвоить Chat ID и username игроку</h2>
                <div className="mt-2 text-sm text-gray-600 space-y-1">
                  <div>Имя: <strong>{displayName}</strong></div>
                  <div>Chat ID: <strong>{chatId}</strong></div>
                  {username && <div>Username: <strong>@{username}</strong></div>}
                </div>
              </div>
              
              <div className="p-6 flex-1 overflow-hidden flex flex-col">
                <div className="mb-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      placeholder="Поиск игрока по имени..."
                      value={playersSearch}
                      onChange={(e) => {
                        setPlayersSearch(e.target.value);
                        setPlayersPagination(prev => ({ ...prev, page: 1 })); // Сбрасываем на первую страницу при поиске
                      }}
                      className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={playersNoIdOnly}
                        onChange={(e) => setPlayersNoIdOnly(e.target.checked)}
                        className="h-4 w-4"
                      />
                      no ID
                    </label>
                  </div>
                  {playersForRender.length > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      Найдено игроков: {playersForRender.length}
                    </div>
                  )}
                </div>
                
                <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg" style={{ maxHeight: '400px' }}>
                  {playersLoading ? (
                    <div className="p-8 text-center text-gray-500">Загрузка игроков...</div>
                  ) : playersForRender.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">Игроки не найдены</div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {playersForRender.map((player) => {
                        const accounts = playerAccountsMap[player.id] || [];
                        const hasAccounts = accounts.length > 0 || player.telegramChatId;
                        
                        return (
                          <div
                            key={player.id}
                            className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                            onClick={() => handleAssignPlayer(player.id)}
                          >
                            <div>
                              <div className="font-medium text-gray-900">{player.name}</div>
                              {player.email && (
                                <div className="text-sm text-gray-500">{player.email}</div>
                              )}
                              {hasAccounts && (
                                <div className="text-xs text-orange-600 mt-1">
                                  {accounts.length > 0 ? (
                                    <>
                                      Уже есть {accounts.length} Telegram аккаунт{accounts.length > 1 ? 'ов' : ''}:
                                      {accounts.map((acc, idx) => (
                                        <span key={acc.id || `${acc.chatId}_${idx}`}>
                                          {idx > 0 && ', '}
                                          {acc.chatId}
                                          {acc.isPrimary && ' (основной)'}
                                        </span>
                                      ))}
                                    </>
                                  ) : player.telegramChatId ? (
                                    <>Уже есть Chat ID: {player.telegramChatId}</>
                                  ) : null}
                                </div>
                              )}
                            </div>
                            <button
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAssignPlayer(player.id);
                              }}
                            >
                              Выбрать
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {/* Пагинация игроков в модальном окне */}
                {playersPagination.pages > 1 && !playersSearchDebounced && (
                  <div className="mt-4 flex justify-center items-center space-x-2">
                    <button
                      onClick={() => setPlayersPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={playersPagination.page === 1}
                      className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Назад
                    </button>
                    
                    <span className="text-sm text-gray-600">
                      Страница {playersPagination.page} из {playersPagination.pages}
                    </span>
                    
                    <button
                      onClick={() => setPlayersPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                      disabled={playersPagination.page === playersPagination.pages}
                      className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Вперед
                    </button>
                  </div>
                )}
              </div>
              
              <div className="p-6 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => {
                    setShowPlayerAssignModal(false);
                    setSelectedMessage(null);
                    setPlayersSearch('');
                    setPlayersNoIdOnly(false);
                    setPlayersPagination({ page: 1, limit: 200, total: 0, pages: 0 });
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Модальное окно с деталями сообщения */}
      {showMessageDetailsModal && selectedMessageDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowMessageDetailsModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Детали сообщения</h2>
                  <div className="mt-1 text-sm text-gray-500">
                    {formatDate(selectedMessageDetails.createdAt)}
                  </div>
                </div>
                <button
                  onClick={() => setShowMessageDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="space-y-6">
                {/* Основная информация */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Основная информация</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Направление:</span>
                      <span>{getDirectionBadge(selectedMessageDetails.direction)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Тип:</span>
                      <span className="text-sm font-medium text-gray-900">{getTypeLabel(selectedMessageDetails.type)}</span>
                    </div>
                    {selectedMessageDetails.status && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Статус:</span>
                        <span>{getStatusBadge(selectedMessageDetails.status)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Chat ID:</span>
                      <span className="text-sm font-mono text-gray-900">{selectedMessageDetails.chatId}</span>
                    </div>
                    {selectedMessageDetails.messageId && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Message ID:</span>
                        <span className="text-sm font-mono text-gray-900">{selectedMessageDetails.messageId}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Игрок */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Игрок</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="text-sm font-medium text-gray-900">
                      {getPlayerDisplayName(selectedMessageDetails)}
                      {selectedMessageDetails.metadata && (() => {
                        try {
                          const meta = JSON.parse(selectedMessageDetails.metadata);
                          if (meta.note === 'Незарегистрированный пользователь' || 
                              (selectedMessageDetails.player.name === 'Незарегистрированные пользователи (Telegram)' && meta.userInfo)) {
                            return (
                              <span className="ml-2 inline-block px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Незарегистрирован
                              </span>
                            );
                          }
                          if (meta.note && meta.note.includes('Сообщение из')) {
                            return (
                              <span className="ml-2 inline-block px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                {meta.note}
                              </span>
                            );
                          }
                        } catch (e) {}
                        return null;
                      })()}
                    </div>
                    {selectedMessageDetails.player.telegramUsername && selectedMessageDetails.player.name !== 'Незарегистрированные пользователи (Telegram)' && (
                      <div className="text-sm text-gray-600">{selectedMessageDetails.player.telegramUsername}</div>
                    )}
                    {selectedMessageDetails.player.telegramChatId && (
                      <div className="text-xs text-gray-500">Chat ID: {selectedMessageDetails.player.telegramChatId}</div>
                    )}
                    {selectedMessageDetails.metadata && (() => {
                      try {
                        const meta = JSON.parse(selectedMessageDetails.metadata);
                        if (meta.userInfo) {
                          return (
                            <>
                              {meta.userInfo.chatId && (
                                <div className="text-xs text-gray-500">Chat ID: {meta.userInfo.chatId}</div>
                              )}
                              {meta.userInfo.username && !getPlayerDisplayName(selectedMessageDetails).includes(`@${meta.userInfo.username}`) && (
                                <div className="text-xs text-gray-500">Username: @{meta.userInfo.username}</div>
                              )}
                            </>
                          );
                        }
                      } catch (e) {}
                      return null;
                    })()}
                  </div>
                </div>

                {/* Сообщение */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Текст сообщения</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                      {selectedMessageDetails.text || '(пусто)'}
                    </p>
                    {selectedMessageDetails.errorMessage && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="text-sm font-medium text-red-800 mb-1">Ошибка:</div>
                        <div className="text-sm text-red-700">{selectedMessageDetails.errorMessage}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Контекст */}
                {(selectedMessageDetails.task || selectedMessageDetails.tournament || selectedMessageDetails.match) && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Контекст</h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      {selectedMessageDetails.task && (
                        <div>
                          <div className="text-sm font-medium text-gray-900 mb-2">Задача подбора соперника</div>
                          <div className="text-xs text-gray-600 space-y-1">
                            <div>Турнир: {selectedMessageDetails.task.tournament.name}</div>
                            <div>Группа: {selectedMessageDetails.task.group.name} ({selectedMessageDetails.task.group.level})</div>
                            <div>Игрок: {selectedMessageDetails.task.activePlayer.name}</div>
                            <div>Соперник: {selectedMessageDetails.task.opponentPlayer.name}</div>
                          </div>
                        </div>
                      )}
                      {selectedMessageDetails.tournament && !selectedMessageDetails.task && (
                        <div>
                          <div className="text-sm font-medium text-gray-900 mb-2">Турнир</div>
                          <div className="text-xs text-gray-600">{selectedMessageDetails.tournament.name}</div>
                        </div>
                      )}
                      {selectedMessageDetails.match && (
                        <div>
                          <div className="text-sm font-medium text-gray-900 mb-2">Матч</div>
                          <div className="text-xs text-gray-600 space-y-1">
                            {selectedMessageDetails.match.matchCode && (
                              <div>Код матча: {selectedMessageDetails.match.matchCode}</div>
                            )}
                            {selectedMessageDetails.match.player1 && (
                              <div>Игрок 1: {selectedMessageDetails.match.player1.name}</div>
                            )}
                            {selectedMessageDetails.match.player2 && (
                              <div>Игрок 2: {selectedMessageDetails.match.player2.name}</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Метаданные */}
                {selectedMessageDetails.metadata && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Метаданные</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <pre className="text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap break-words">
                        {JSON.stringify(JSON.parse(selectedMessageDetails.metadata), null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Дополнительная информация */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Дополнительная информация</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Создано:</span>
                      <span className="text-gray-900">{new Date(selectedMessageDetails.createdAt).toLocaleString('ru-RU')}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Обновлено:</span>
                      <span className="text-gray-900">{new Date(selectedMessageDetails.updatedAt).toLocaleString('ru-RU')}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">ID записи:</span>
                      <span className="text-gray-900 font-mono text-xs">{selectedMessageDetails.id}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowMessageDetailsModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramLogs;

